/**
 * Hero Slider block entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS.
 * Swiper is lazy-loaded only when the block is present on the page. GSAP animates
 * the first slide's content in on load, same as the (non-slider) Hero block.
 */

import './index.scss';

const swiperEls = document.querySelectorAll('[data-hero-slider-swiper]');

if (swiperEls.length) {
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	(async () => {
		const [{ default: Swiper }, { A11y, Autoplay, Keyboard, Pagination }] = await Promise.all([
			import('swiper'),
			import('swiper/modules'),
			import('swiper/css'),
		]);

		swiperEls.forEach((el) => {
			const slideCount = el.querySelectorAll('.swiper-slide').length;
			const paginationEl = el.querySelector('[data-hero-slider-dots]');

			if (slideCount < 2) return;

			new Swiper(el, {
				modules: [Pagination, Keyboard, Autoplay, A11y],
				slidesPerView: 1,
				loop: true,
				speed: prefersReducedMotion ? 0 : 600,
				keyboard: { enabled: true },
				a11y: { enabled: true },
				autoplay: prefersReducedMotion
					? false
					: { delay: 6000, disableOnInteraction: true, pauseOnMouseEnter: true },
				pagination: {
					el: paginationEl,
					clickable: true,
					bulletClass: 'hero-slider__dot',
					bulletActiveClass: 'is-active',
					renderBullet: (index, className) =>
						`<button type="button" class="${className}" aria-label="Show slide ${index + 1}"></button>`,
				},
			});
		});
	})();

	if (!prefersReducedMotion) {
		// Captured before the Swiper import resolves, so loop-mode clone slides
		// (prepended once Swiper initializes) can't be picked up by mistake.
		const firstSlides = Array.from(swiperEls).map((el) => ({
			el,
			slide: el.querySelector('.hero-slider__slide'),
		}));

		(async () => {
			const { gsap } = await import('gsap');

			firstSlides.forEach(({ el, slide }) => {
				if (!slide) return;

				const content = slide.querySelector('.hero-slider__content');
				const bg = slide.querySelector('.hero-slider__bg');

				const ctx = gsap.context(() => {
					const tl = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.9 } });

					if (bg) {
						gsap.set(bg, { scale: 1.08 });
						tl.to(bg, { scale: 1, duration: 1.6, ease: 'power2.out' }, 0);
					}

					if (content) {
						gsap.set(content, { opacity: 0, y: 30 });
						tl.to(content, { opacity: 1, y: 0 }, 0.3);
					}
				}, el);

				el._gsapCtx = ctx;
			});
		})();
	}
}
